import { useCallback, useMemo, useState } from 'react'
import { intl } from '../../../../i18n'
import {
  CardData,
  Swimlane,
  SwimlaneCumulationData,
  SwimlaneStatusCumulationMap,
  UNASSIGNED_SWIMLANE_ID,
} from '../Kanban'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'

export const useSwimlanes = (
  cards: { [id: string]: CardData },
  filter: (card: CardData) => boolean
) => {
  const [collapseState, setCollapseState] = useState<Record<string, boolean>>(
    {}
  )

  const swimlanes = useMemo(() => {
    const responsibleMap = new Map<string, Swimlane>()

    Object.values(cards).forEach(card => {
      if (!card) return
      const resp = card.content.responsible
      const id = resp?.uuid ?? UNASSIGNED_SWIMLANE_ID

      if (!responsibleMap.has(id)) {
        responsibleMap.set(id, {
          id,
          responsibleUuid: resp?.uuid ?? null,
          responsibleName:
            resp?.name ?? intl.formatMessage({ id: 'wbsItem.unassigned' }),
          responsibleIconUrl: resp?.iconUrl,
        })
      }
    })

    // ソート: 名前順（日本語ロケール）、未割り当ては最後
    return Array.from(responsibleMap.values()).sort((a, b) => {
      if (a.responsibleUuid === null) return 1
      if (b.responsibleUuid === null) return -1
      return a.responsibleName.localeCompare(b.responsibleName, 'ja')
    })
  }, [cards])

  // スイムレーンごとの集計データ
  const swimlaneCumulation = useMemo(() => {
    const result: Record<string, SwimlaneCumulationData> = {}

    swimlanes.forEach(sl => {
      const slCards = Object.values(cards).filter(c => {
        if (!c || !filter(c)) return false
        if (c.content.status === WbsItemStatus.DISCARD) return false
        const cardRespId = c.content.responsible?.uuid ?? null
        return cardRespId === sl.responsibleUuid
      })

      result[sl.id] = {
        count: slCards.length,
        estimatedHour: slCards.reduce(
          (sum, c) => sum + (c.content.estimatedWorkload?.hour ?? 0),
          0
        ),
        actualHour: slCards.reduce(
          (sum, c) => sum + (c.content.actualHour ?? 0),
          0
        ),
      }
    })
    return result
  }, [swimlanes, cards, filter])

  // スイムレーン×ステータスごとの集計データ
  const swimlaneStatusCumulation = useMemo(() => {
    const result: SwimlaneStatusCumulationMap = {}

    swimlanes.forEach(sl => {
      result[sl.id] = {}

      // このスイムレーンに属するカードを取得
      const slCards = Object.values(cards).filter(c => {
        if (!c || !filter(c)) return false
        const cardRespId = c.content.responsible?.uuid ?? null
        return cardRespId === sl.responsibleUuid
      })

      // ステータスごとに集計
      const statusMap = new Map<string, CardData[]>()
      slCards.forEach(card => {
        const status = card.content.status
        if (!status) return
        if (!statusMap.has(status)) {
          statusMap.set(status, [])
        }
        statusMap.get(status)!.push(card)
      })

      statusMap.forEach((statusCards, status) => {
        result[sl.id][status] = {
          count: statusCards.length,
          estimatedHour: statusCards.reduce(
            (sum, c) => sum + (c.content.estimatedWorkload?.hour ?? 0),
            0
          ),
          actualHour: statusCards.reduce(
            (sum, c) => sum + (c.content.actualHour ?? 0),
            0
          ),
        }
      })
    })

    return result
  }, [swimlanes, cards, filter])

  const toggleCollapse = useCallback((id: string) => {
    setCollapseState(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  return {
    swimlanes,
    swimlaneCumulation,
    swimlaneStatusCumulation,
    collapseState,
    toggleCollapse,
  }
}
